import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar, MapPin, Copy, ExternalLink, Download, Link as LinkIcon, Upload, Save } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';
import type { Evento, DetalhesEvento, ExecucaoEvento } from '@/lib/types';

type Aba = 'convite' | 'info' | 'progresso';

export function EventoDetalhePortal() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const portalSid = usePortalSid();
  const sid = portalSid ?? user?.solicitanteId ?? '';
  const [searchParams] = useSearchParams();
  const asSuffix = searchParams.get('as') ? `?as=${searchParams.get('as')}` : '';
  const qc = useQueryClient();
  const [aba, setAba] = useState<Aba>('convite');

  const eventosQ = useQuery({ queryKey: ['portal', 'eventos'], queryFn: () => api.eventos.listPortal().then(r => r.data) });
  const detalhesQ = useQuery({
    queryKey: ['portal', 'detalhes', id, sid],
    queryFn: () => api.eventos.getDetalhes(id!, sid).then(r => r.data),
    enabled: !!id,
  });

  const evento: Evento | undefined = useMemo(() => (eventosQ.data ?? []).find(e => e.id === id), [eventosQ.data, id]);

  // Progresso Tipo B — cards vinculados ao evento
  const execQ = useQuery({
    queryKey: ['portal', 'execucao', id],
    queryFn: () => api.eventos.execucao(id!).then((r: any) => r.data ?? r),
    enabled: !!id,
    staleTime: 60_000,
  });
  const exec = execQ.data as ExecucaoEvento | null | undefined;

  // form state
  const [form, setForm] = useState<Partial<DetalhesEvento>>({});
  useEffect(() => {
    if (detalhesQ.data) setForm(detalhesQ.data);
  }, [detalhesQ.data]);

  const save = useMutation({
    mutationFn: (body: Partial<DetalhesEvento>) => api.eventos.putDetalhes(id!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'detalhes', id, sid] });
    },
  });

  const dias = evento?.dataInicio ? Math.ceil((new Date(evento.dataInicio).getTime() - Date.now()) / 86400000) : null;

  if (!evento) {
    return (
      <div className="p-6">
        <Link to={`/portal/eventos${asSuffix}`} className="text-xs text-blue-light hover:underline inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar
        </Link>
        <div className="bg-panel border border-border rounded-lg p-8 text-center text-muted">
          {eventosQ.isLoading ? 'Carregando…' : 'Evento não encontrado'}
        </div>
      </div>
    );
  }

  function set<K extends keyof DetalhesEvento>(k: K, v: DetalhesEvento[K]) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  function onSave(e: React.FormEvent) {
    e.preventDefault();
    save.mutate(form);
  }

  return (
    <div className="p-6 space-y-3">
      <Link to={`/portal/eventos${asSuffix}`} className="text-xs text-blue-light hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Voltar pra Eventos
      </Link>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#001B3A] to-[#1565A8] text-white rounded-xl p-5 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-blue-400/30 blur-3xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white/15 backdrop-blur">
              📅 Próximo evento
            </span>
            <h1 className="text-2xl font-extrabold mt-2">{evento.nome}</h1>
            <div className="text-xs opacity-85 mt-1.5 space-x-3">
              {evento.dataInicio && <span><Calendar className="w-3 h-3 inline mr-1" />{new Date(evento.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
              {evento.local && <span><MapPin className="w-3 h-3 inline mr-1" />{evento.local}</span>}
            </div>
          </div>
          {dias !== null && dias > 0 && (
            <div className="text-center px-4 py-3 rounded-lg bg-white/15 backdrop-blur border border-white/20">
              <div className="text-3xl font-extrabold leading-none">{dias}</div>
              <div className="text-[10px] uppercase tracking-wider opacity-80 mt-1">dias pra começar</div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-9 border-b border-border">
        {(['convite', 'info', 'progresso'] as Aba[]).map(t => (
          <button key={t} type="button" onClick={() => setAba(t)}
            className={clsx(
              'py-3.5 text-sm font-medium border-b-2 -mb-px transition-colors bg-transparent',
              aba === t ? 'text-blue-light border-blue font-semibold' : 'text-muted border-transparent hover:text-text',
            )}
            style={{ appearance: 'none', border: 0, outline: 0 }}>
            {t === 'convite' ? 'Convite & Inscrição' : t === 'info' ? 'Informações' : 'Progresso'}
          </button>
        ))}
      </div>

      {aba === 'convite' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-2">
          {/* Coluna esquerda: convite */}
          <div>
            <h4 className="text-[10px] font-bold text-muted uppercase tracking-wider mb-2.5">Convite oficial</h4>
            <div className="bg-panel border border-border rounded-xl overflow-hidden">
              <div className="bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] text-white p-6 relative">
                <div className="text-[9px] text-right opacity-70">REALIZAÇÃO ◆ APEX</div>
                <h3 className="text-2xl font-extrabold mt-3 leading-tight">{evento.nome}</h3>
                {evento.descricao && (
                  <p className="text-[11px] mt-2 bg-white/10 rounded p-2">{evento.descricao}</p>
                )}
                {evento.dataInicio && (
                  <div className="text-xs mt-3 font-semibold">{new Date(evento.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                )}
                {evento.local && <div className="text-[10px] opacity-85">{evento.local}</div>}
              </div>
              <div className="p-4 border-t border-border">
                {evento.conviteUrl ? (
                  <a href={evento.conviteUrl} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-text bg-white border border-border-light rounded-md hover:bg-panel-2 transition">
                    <Download className="w-3.5 h-3.5" /> Baixar convite
                  </a>
                ) : (
                  <span className="text-[11px] text-muted italic">Convite ainda não disponível</span>
                )}
              </div>
            </div>
          </div>

          {/* Coluna direita: cupom + botões + jantar exclusivo */}
          <div className="flex flex-col gap-3.5">
            <h4 className="invisible text-[10px] mb-0 h-[18px] leading-[18px]">·</h4>
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-300 rounded-xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-900">CUPOM EXCLUSIVO</div>
              {evento.cupomCvent ? (
                <>
                  <div className="text-4xl font-extrabold tracking-widest text-blue-900 mt-2 leading-none">{evento.cupomCvent}</div>
                  <div className="text-xs text-blue-800 mt-2">Use este cupom no formulário de inscrição</div>
                  <button type="button" onClick={() => navigator.clipboard?.writeText(evento.cupomCvent ?? '')}
                    className="mt-3 inline-flex items-center gap-1.5 bg-white border border-blue-300 text-blue-800 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-blue-50">
                    <Copy className="w-3 h-3" /> Copiar cupom
                  </button>
                </>
              ) : (
                <div className="text-xs text-blue-800 italic mt-2">Cupom ainda não disponível</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {evento.linkCvent ? (
                <a href={evento.linkCvent} target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-border rounded-md text-xs font-semibold text-text hover:border-blue/50">
                  <ExternalLink className="w-3 h-3" /> Inscrição Cvent
                </a>
              ) : (
                <div className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-border rounded-md text-xs text-muted">
                  Cvent indisponível
                </div>
              )}
              <button type="button"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-border rounded-md text-xs font-semibold text-text hover:border-blue/50">
                👥 Confira inscritos
              </button>
            </div>

            {/* Sub-blocos customizados (preview de Jantar Exclusivo etc) */}
            {detalhesQ.data && detalhesQ.data.subBlocosCustomizados && (
              <div className="bg-panel border border-border rounded-xl p-4">
                <h5 className="text-xs font-bold text-text mb-2.5 flex items-center gap-1.5">🎁 Bloco customizado</h5>
                <pre className="text-[10.5px] text-text-2 whitespace-pre-wrap font-sans">{detalhesQ.data.subBlocosCustomizados}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {aba === 'progresso' && (
        <div className="space-y-4 pt-2">
          {execQ.isLoading && <div className="text-sm text-muted py-8 text-center">Carregando progresso…</div>}
          {exec && exec.totalCards === 0 && (
            <div className="bg-panel border border-border rounded-xl p-8 text-center text-muted text-sm">
              Nenhuma entrega vinculada a este evento ainda.
            </div>
          )}
          {exec && exec.totalCards > 0 && (
            <>
              {/* Barra geral */}
              <div className="bg-panel border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-text">Progresso geral</span>
                  <span className="text-sm font-bold text-blue-light">{exec.progressoGeral}%</span>
                </div>
                <div className="w-full h-2.5 bg-border rounded-full overflow-hidden">
                  <div className="h-full bg-blue rounded-full transition-all" style={{ width: `${exec.progressoGeral}%` }} />
                </div>
                <div className="text-[11px] text-muted mt-1.5">{exec.totalCards} entregas em andamento</div>
              </div>

              {/* Por área */}
              {exec.porArea.map(area => (
                <div key={area.areaId} className="bg-panel border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 bg-panel-2/40 border-b border-border">
                    <span className="text-xs font-bold text-text flex-1">{area.areaNome}</span>
                    <div className="w-20 h-1.5 bg-border rounded-full overflow-hidden">
                      <div className={clsx('h-full rounded-full', area.percentual >= 80 ? 'bg-green' : area.percentual >= 40 ? 'bg-blue' : 'bg-orange')}
                        style={{ width: `${area.percentual}%` }} />
                    </div>
                    <span className="text-[11px] font-bold text-muted">{area.percentual}%</span>
                  </div>
                  <div className="divide-y divide-border">
                    {area.cards.map(card => {
                      const pct = card.subItems.total > 0 ? Math.round((card.subItems.concluido / card.subItems.total) * 100) : 0;
                      return (
                        <div key={card.id} className="flex items-center gap-3 px-4 py-2.5">
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-medium text-text truncate">{card.titulo}</div>
                            <div className="text-[10px] text-muted mt-0.5">
                              {card.status === 'Publicada' ? '✓ Finalizado' : card.status}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[10px] font-bold">{pct}%</div>
                            <div className="w-14 h-1 bg-border rounded-full overflow-hidden mt-0.5">
                              <div className="h-full bg-green rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {aba === 'info' && (
        <form onSubmit={onSave} className="space-y-5 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Field label="Nome do representante">
              <input type="text" value={form.nomeRepresentante ?? ''} onChange={e => set('nomeRepresentante', e.target.value)}
                placeholder="Nome completo" className="input w-full text-sm" />
            </Field>
            <Field label="Cargo">
              <input type="text" value={form.cargoRepresentante ?? ''} onChange={e => set('cargoRepresentante', e.target.value)}
                placeholder="Diretor, CEO…" className="input w-full text-sm" />
            </Field>
            <Field label="Produto / serviço a destacar" full>
              <input type="text" value={form.produtoServicoDestacar ?? ''} onChange={e => set('produtoServicoDestacar', e.target.value)}
                placeholder="Ex: Gestão de ativos, crédito estruturado…" className="input w-full text-sm" />
            </Field>
            <Field label="Participação em painel?" full>
              <select value={form.participacaoPainel ?? ''} onChange={e => set('participacaoPainel', e.target.value as DetalhesEvento['participacaoPainel'])}
                className="input w-full text-sm">
                <option value="">Selecione…</option>
                <option value="Sim — confirmado">Sim — confirmado</option>
                <option value="Sim — em análise">Sim — em análise</option>
                <option value="Não">Não</option>
                <option value="A definir">A definir</option>
              </select>
            </Field>
          </div>

          <hr className="border-border" />
          <div className="text-[11px] font-bold text-muted uppercase tracking-widest">Materiais pendentes</div>

          <MaterialField label="Artes para materiais gráficos"
            value={form.materialArtesGraficas ?? ''}
            onChange={v => set('materialArtesGraficas', v)} />

          <MaterialField label="Vídeo institucional 30s"
            value={form.materialVideoInstitucional ?? ''}
            onChange={v => set('materialVideoInstitucional', v)} />

          <Field label="Confirmação de palestrante" full>
            <input type="text" value={form.confirmacaoPalestrante ?? ''} onChange={e => set('confirmacaoPalestrante', e.target.value)}
              placeholder="Nome do palestrante e tema confirmado" className="input w-full text-sm" />
          </Field>

          <button type="submit" disabled={save.isPending}
            className="bg-blue hover:bg-blue-dark text-white text-sm font-bold px-6 py-2.5 rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
            <Save className="w-4 h-4" />
            {save.isPending ? 'Salvando…' : save.isSuccess ? 'Salvo ✓' : 'Salvar informações'}
          </button>
        </form>
      )}
    </div>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={clsx('flex flex-col gap-1.5', full && 'md:col-span-2')}>
      <label className="text-[10px] font-bold text-muted uppercase tracking-widest">{label}</label>
      {children}
    </div>
  );
}

function MaterialField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [mode, setMode] = useState<'link' | 'upload'>('link');
  return (
    <div>
      <label className="text-[11px] font-bold text-muted uppercase tracking-widest mb-2 block">{label}</label>
      <div className="flex gap-1.5 mb-2">
        <button type="button" onClick={() => setMode('link')}
          className={clsx('px-3 py-1.5 text-[12px] font-semibold rounded border inline-flex items-center gap-1.5',
            mode === 'link' ? 'bg-blue text-white border-blue' : 'bg-white border-border text-text-2')}>
          <LinkIcon className="w-3 h-3" /> Link
        </button>
        <button type="button" onClick={() => setMode('upload')} disabled
          className={clsx('px-3 py-1.5 text-[12px] font-semibold rounded border inline-flex items-center gap-1.5 opacity-50 cursor-not-allowed',
            mode === 'upload' ? 'bg-blue text-white border-blue' : 'bg-white border-border text-text-2')}>
          <Upload className="w-3 h-3" /> Upload
        </button>
      </div>
      <input type="url" value={value} onChange={e => onChange(e.target.value)}
        placeholder="Cole o link (Google Drive, Dropbox, WeTransfer…)"
        className="input w-full text-sm" />
    </div>
  );
}
